/**
 * Undergrowth — ferns and grass tufts on the ground.
 *
 * A real jungle floor is not bare dirt, and bare dirt was the loudest thing still
 * reading as fake once the ground was textured. Crossed quads with an alpha-cut leaf
 * texture, instanced so a few thousand tufts cost one draw call.
 */
import * as THREE from 'three'
import { MAP_HALF_EXTENT, WATER } from './map'
import { heightAt } from './terrain'

export interface UndergrowthStyle {
  color: number
  /** How many tufts to scatter. Desert and ocean want few or none. */
  count: number
  /** Height in metres of a typical tuft. */
  height: number
  waterRadius: number
}

/** A soft-edged blade cluster, drawn once and reused as an alpha map. */
function createBladeTexture(): THREE.CanvasTexture {
  // 64px gave every blade a width of one or two pixels, which is why a tuft read as a
  // handful of flat sticks: there was no room in the texture for a blade to have a
  // shape. At 256 a blade has an edge, a taper and a soft tip.
  const size = 256
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  if (!context) throw new Error('2d context unavailable')

  context.clearRect(0, 0, size, size)
  // Many thin, curved blades rather than a few wide wedges — the wide ones read as
  // flat triangles the moment you saw a tuft edge-on.
  for (let i = 0; i < 34; i++) {
    const baseX = size * (0.08 + (i / 34) * 0.84)
    const lean = (Math.random() - 0.5) * size * 0.42
    const tipY = size * (0.08 + Math.random() * 0.3)
    const width = size * (0.016 + Math.random() * 0.014)
    // Blades are lighter at the tip, which is most of what makes grass look like grass.
    const shade = 190 + Math.floor(Math.random() * 60)
    context.strokeStyle = `rgb(${shade},${shade},${shade})`
    context.lineWidth = width * 2
    context.lineCap = 'round'
    context.beginPath()
    context.moveTo(baseX, size)
    context.quadraticCurveTo(baseX + lean * 0.4, size * 0.55, baseX + lean, tipY)
    context.stroke()
  }
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  texture.anisotropy = 4
  return texture
}

export function createUndergrowth(style: UndergrowthStyle, seed = 4242): THREE.InstancedMesh {
  let a = seed >>> 0
  const random = (): number => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }

  const blade = createBladeTexture()
  // Two quads crossed at right angles, so a tuft has volume from any viewing angle.
  const plane = new THREE.PlaneGeometry(1, 1)
  plane.translate(0, 0.5, 0)
  const second = plane.clone()
  second.rotateY(Math.PI / 3)
  const third = plane.clone()
  third.rotateY((Math.PI * 2) / 3)
  // Three crossed quads, not two: with two there is an angle where a tuft is edge-on
  // to both and collapses into a flat sliver.
  const geometry = mergeGeometries(mergeGeometries(plane, second), third)
  // Point every normal straight up. A grass quad's true normal faces sideways, so half
  // of every tuft turns away from the sun and goes flat and dark --- the thing that
  // reads as cardboard. Borrowing the ground's upward normal is what real grass
  // rendering does, and it makes a tuft sit in the light the ground sits in.
  const normal = geometry.getAttribute('normal') as THREE.BufferAttribute
  for (let i = 0; i < normal.count; i++) normal.setXYZ(i, 0, 1, 0)
  normal.needsUpdate = true

  const material = new THREE.MeshStandardMaterial({
    map: blade,
    alphaMap: blade,
    color: style.color,
    alphaTest: 0.42,
    side: THREE.DoubleSide,
    roughness: 0.9,
  })

  const capacity = Math.max(style.count, 1)
  const mesh = new THREE.InstancedMesh(geometry, material, capacity)
  mesh.receiveShadow = true

  const matrix = new THREE.Matrix4()
  const position = new THREE.Vector3()
  const quaternion = new THREE.Quaternion()
  const scale = new THREE.Vector3()
  const up = new THREE.Vector3(0, 1, 0)
  const edge = MAP_HALF_EXTENT - 1

  let placed = 0
  let attempts = 0
  while (placed < style.count && attempts < style.count * 8) {
    attempts++
    const x = (random() * 2 - 1) * edge
    const z = (random() * 2 - 1) * edge
    if (style.waterRadius > 0 && Math.hypot(x - WATER.x, z - WATER.z) < style.waterRadius) continue

    const size = style.height * (0.6 + random() * 0.8)
    position.set(x, heightAt(x, z), z)
    quaternion.setFromAxisAngle(up, random() * Math.PI * 2)
    scale.set(size * (0.8 + random() * 0.5), size, size)
    matrix.compose(position, quaternion, scale)
    mesh.setMatrixAt(placed, matrix)
    placed++
  }

  mesh.count = placed
  mesh.instanceMatrix.needsUpdate = true
  mesh.frustumCulled = false
  return mesh
}

/** Minimal two-geometry merge — avoids pulling in the addons build for one call. */
function mergeGeometries(a: THREE.BufferGeometry, b: THREE.BufferGeometry): THREE.BufferGeometry {
  const merged = new THREE.BufferGeometry()
  for (const name of ['position', 'normal', 'uv']) {
    const attrA = a.getAttribute(name) as THREE.BufferAttribute
    const attrB = b.getAttribute(name) as THREE.BufferAttribute
    const combined = new Float32Array(attrA.array.length + attrB.array.length)
    combined.set(attrA.array as Float32Array, 0)
    combined.set(attrB.array as Float32Array, attrA.array.length)
    merged.setAttribute(name, new THREE.BufferAttribute(combined, attrA.itemSize))
  }
  const indexA = a.getIndex()
  const indexB = b.getIndex()
  if (indexA && indexB) {
    const offset = (a.getAttribute('position') as THREE.BufferAttribute).count
    const combined = new Uint16Array(indexA.count + indexB.count)
    for (let i = 0; i < indexA.count; i++) combined[i] = indexA.getX(i)
    for (let i = 0; i < indexB.count; i++) combined[indexA.count + i] = indexB.getX(i) + offset
    merged.setIndex(new THREE.BufferAttribute(combined, 1))
  }
  return merged
}
